import {
	LayoutServicePageState,
	useSitecoreContext as useSitecoreContextNative,
} from '@sitecore-jss/sitecore-jss-nextjs';

import type {
	CustomLayoutServiceContext,
	LanguageCode,
	SitecoreRouteData,
} from 'models/sitecore';
import { empty } from 'utils/helpers';

/**
 * This is the same shape as the regular SitecoreContext
 * but with the addition of our custom LayoutServiceData and RouteData types.
 */
interface SitecoreContext extends CustomLayoutServiceContext {
	itemId?: string;
	route?: SitecoreRouteData;
}

// Wrap the native hook to add more detailed typing.
export function useSitecoreContext() {
	const { sitecoreContext } = useSitecoreContextNative();
	if (!sitecoreContext) {
		throw new Error(
			'useSitecoreContext must be used within a SitecoreContextProvider',
		);
	}
	return {
		sitecoreContext: sitecoreContext as unknown as SitecoreContext,
	};
}

export function useRouteFields() {
	const { sitecoreContext } = useSitecoreContext();
	return sitecoreContext.route?.fields;
}

export function useIsCompanySite() {
	const { sitecoreContext } = useSitecoreContext();
	return sitecoreContext.juridicalType === 'Company';
}

export function useMarketCode() {
	const { sitecoreContext } = useSitecoreContext();
	return sitecoreContext.marketCode;
}

export function useLocale() {
	const { sitecoreContext } = useSitecoreContext();
	return {
		/** ISO 639-1 language code */
		language: sitecoreContext.language.split('-')[0] as LanguageCode,
		/** BCP 47 language tag (ISO 639-1 language code + ISO-3166 region code) */
		locale: sitecoreContext.language,
	};
}

export function useLoginOptions() {
	const { sitecoreContext } = useSitecoreContext();
	return sitecoreContext.authenticationMethods;
}

export function useFeatureToggle() {
	const { sitecoreContext } = useSitecoreContext();
	return sitecoreContext.featureToggles;
}

export function useGlobalLinks() {
	const { sitecoreContext } = useSitecoreContext();
	return sitecoreContext.globalLinks || empty.object;
}

/** If currently rendering in Sitecore's Experience Editor. */
export function useIsEditing() {
	const { sitecoreContext } = useSitecoreContext();
	return sitecoreContext.pageEditing ?? false;
}

/** If currently rendering in Sitecore's preview mode. */
export function useIsPreviewing() {
	const { sitecoreContext } = useSitecoreContext();
	return sitecoreContext.pageState === LayoutServicePageState.Preview;
}

export function useClientSettings() {
	const { sitecoreContext } = useSitecoreContext();
	return sitecoreContext.clientSettings || empty.object;
}
